import { FC, useMemo, useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import { useParams } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { useStore } from 'hooks/useRootStore';
import { useMyTranslations } from 'hooks/useMyTranslations';
import { AppLoader } from 'components/Core/AppLoader';
import { EntityTitle } from 'components/Core/EntityTitle';
import ScriptBasicForm from 'components/Forms/ScriptBasicForm/ScriptBasicForm';
import TranslationsForMobileForm from 'components/Forms/TranslationsForMobileForm/TranslationsForMobileForm';
import { ScriptType } from 'stores/scripts/types';
import { TranslatesObject } from 'types/common';
import { DEFAULT_SCRIPT_TAB, tabs } from './constants';
import { scriptTranslations } from './translations';
import { ScriptTab, ScriptTransFields } from './types';
import styles from './script.module.scss';

const Script: FC = () => {
  const {
    scriptsStore: { getScriptsData, scriptData, changeScriptMessageTranslations },
    alertStore: { showAlert },
  } = useStore();

  const { t } = useMyTranslations<ScriptTransFields>([scriptTranslations.name]);

  const { id, type } = useParams<{ id: string; type: ScriptType }>();

  const [currentTab, setCurrentTab] = useState<ScriptTab>(DEFAULT_SCRIPT_TAB);

  const { isFetching: isLoading } = useQuery(
    ['getScriptsData', type, id],
    () => (id && type ? getScriptsData({ id, type }) : null),
    {
      enabled: !!id && !!type,
      retry: false,
      refetchOnWindowFocus: false,
    }
  );

  const { mutate: saveTranslations, isLoading: isSaving } = useMutation(changeScriptMessageTranslations, {
    mutationKey: 'changeScriptMessageTranslations',
    onSuccess: () => showAlert(t('translationsSaved'), 'success'),
  });

  const onChangeTab = (newTab: ScriptTab) => {
    if (newTab !== currentTab) {
      setCurrentTab(newTab);
    }
  };

  const onSaveScriptTranslations = (translations: TranslatesObject) => {
    if (!scriptData) return;
    saveTranslations({ type: scriptData.type, translations });
  };

  const component = useMemo(() => {
    if (!id || !type) return null;
    switch (currentTab) {
      case 'basic':
        return <ScriptBasicForm />;
      case 'translations':
        return (
          <TranslationsForMobileForm
            title={t('translations')}
            translations={scriptData?.messageTranslations || {}}
            onSave={onSaveScriptTranslations}
            isLoading={isSaving}
          />
        );
      default:
        return null;
    }
  }, [currentTab, id, type, scriptData, isSaving, onSaveScriptTranslations, t]);

  return (
    <main className={styles.page}>
      <EntityTitle title={scriptData?.name || ''} />
      <div className={styles.container}>
        <div className={styles.filters}>
          {tabs.map((item) => (
            <button
              key={item}
              className={`${styles.filterBtn}  ${currentTab === item && styles.filterBtnCurent}`}
              type='button'
              onClick={() => onChangeTab(item)}
              disabled={isLoading || !id}>
              <span className={styles.btnTitle}>{t(item)}</span>
            </button>
          ))}
        </div>
        {isLoading ? <AppLoader /> : component}
      </div>
    </main>
  );
};

export default observer(Script);
